import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { LayoutDashboard, Store, Boxes, ClipboardList, UsersRound, CalendarClock, CalendarDays, Sparkles, QrCode, ScanLine, Mail, Inbox, Wrench, UserPlus, Lock, Server, LineChart, History, Trash2, ChevronDown, Sprout } from "lucide-react";
import { useRacketLabAdminT } from "../../i18n/racketLabAdminI18n";
import useRLContext from "@/hooks/useRLContext";

/**
 * RLNavSidebar — Sub-sprint Sidebar collapsible.
 *
 * Scoped navigation for the Racket Lab admin shell. Items are
 * partitioned into four collapsible groups (default expanded) to
 * keep the surface readable now that we sit at 17 entries.
 *
 * Existing per-entry `data-testid`s are preserved verbatim so prior
 * Playwright scripts and screenshots keep working.
 *
 * QUICK 1.B i18n migration: literal `label` fields replaced with
 * dotted-path keys (`labelKey`) resolved through `useRacketLabAdminT()`.
 * Routes (`to`), test IDs, icons and `end` flags are untouched.
 *
 * SS-8/CP-1.3 — RBAC sidebar: each item declares its `section` and only
 * renders when the viewer's me-context `sections` include it. Groups
 * with zero visible items collapse away entirely.
 */
const GROUPS = [
  {
    id: "core",
    labelKey: "nav.groups.core",
    testId: "rl-nav-group-core",
    items: [
      { to: "/admin/racket-lab", labelKey: "nav.items.dashboard", icon: LayoutDashboard, end: true, testId: "rl-nav-dashboard", section: "dashboard" },
      { to: "/admin/racket-lab/workforce/shops", labelKey: "nav.items.shops", icon: Store, testId: "rl-nav-shops", section: "shops" },
      { to: "/admin/racket-lab/inventory", labelKey: "nav.items.inventory", icon: Boxes, testId: "rl-nav-inventory", section: "inventory" },
      { to: "/admin/racket-lab/tickets", labelKey: "nav.items.tickets", icon: ClipboardList, testId: "rl-nav-tickets", section: "tickets" },
      { to: "/admin/racket-lab/my-tickets", labelKey: "nav.items.my_tickets", icon: ClipboardList, testId: "rl-nav-my-tickets", section: "my_tickets" },
      { to: "/admin/racket-lab/calendar", labelKey: "nav.items.calendar", icon: CalendarDays, testId: "rl-nav-calendar", section: "calendar" },
    ],
  },
  {
    id: "workforce",
    labelKey: "nav.groups.workforce",
    testId: "rl-nav-group-workforce",
    items: [
      { to: "/admin/racket-lab/workforce/operators", labelKey: "nav.items.operators", icon: UsersRound, testId: "rl-nav-operators", section: "operators" },
      { to: "/admin/racket-lab/workforce/shifts", labelKey: "nav.items.shifts", icon: CalendarClock, testId: "rl-nav-shifts", section: "shifts" },
      { to: "/admin/racket-lab/workforce/capabilities", labelKey: "nav.items.capabilities", icon: Sparkles, testId: "rl-nav-capabilities", section: "capabilities" },
    ],
  },
  {
    id: "qr-notifications",
    labelKey: "nav.groups.qr_notifications",
    testId: "rl-nav-group-qr-notifications",
    items: [
      { to: "/admin/racket-lab/qr-tokens", labelKey: "nav.items.qr_tokens", icon: QrCode, testId: "rl-nav-qr-tokens", section: "qr_tokens" },
      { to: "/admin/racket-lab/qr-tokens/resolve", labelKey: "nav.items.resolve", icon: ScanLine, testId: "rl-nav-resolve-viewer", section: "resolve" },
      { to: "/admin/racket-lab/notifications/envelopes", labelKey: "nav.items.envelopes", icon: Mail, testId: "rl-nav-notifications", section: "envelopes" },
      { to: "/admin/racket-lab/notifications/mock-outbox", labelKey: "nav.items.mock_outbox", icon: Inbox, testId: "rl-nav-mock-outbox", section: "mock_outbox" },
    ],
  },
  {
    id: "ops",
    labelKey: "nav.groups.ops_toolkit",
    testId: "rl-nav-group-ops",
    items: [
      { to: "/admin/racket-lab/ops", labelKey: "nav.items.toolkit", icon: Wrench, testId: "rl-nav-ops", section: "ops" },
      { to: "/admin/racket-lab/ops/promote", labelKey: "nav.items.promote", icon: UserPlus, testId: "rl-nav-ops-promote", section: "ops" },
      { to: "/admin/racket-lab/ops/gating", labelKey: "nav.items.gating", icon: Lock, testId: "rl-nav-ops-gating", section: "ops" },
      { to: "/admin/racket-lab/ops/redis", labelKey: "nav.items.redis", icon: Server, testId: "rl-nav-ops-redis", section: "ops" },
      { to: "/admin/racket-lab/ops/scan-audit", labelKey: "nav.items.scan_audit", icon: LineChart, testId: "rl-nav-ops-scan-audit", section: "ops" },
      { to: "/admin/racket-lab/ops/legacy", labelKey: "nav.items.legacy", icon: History, testId: "rl-nav-ops-legacy", section: "ops" },
      { to: "/admin/racket-lab/ops/retention", labelKey: "nav.items.retention", icon: Trash2, testId: "rl-nav-ops-retention", section: "ops" },
      { to: "/admin/racket-lab/ops/seed-helper", labelKey: "nav.items.seed_helper", icon: Sprout, testId: "rl-nav-ops-seed-helper", section: "ops" },
    ],
  },
];

const Group = ({ group, t, allowed }) => {
  const [open, setOpen] = useState(true);
  const items = group.items.filter((it) => allowed.has(it.section));
  if (!items.length) return null;
  return (
    <section data-testid={group.testId} className="flex w-full flex-col gap-2 md:w-auto">
      <button
        type="button"
        data-testid={`${group.testId}-toggle`}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 self-start rounded-md px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500 hover:bg-slate-100"
        aria-expanded={open}
      >
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "" : "-rotate-90"}`} />
        {t(group.labelKey)}
      </button>
      {open && (
        <div data-testid={`${group.testId}-items`} className="flex flex-wrap gap-1.5">
          {items.map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={it.testId}
                className={({ isActive }) =>
                  `inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium ${
                    isActive ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`
                }
              >
                <Icon className="w-3.5 h-3.5" />
                {t(it.labelKey)}
              </NavLink>
            );
          })}
        </div>
      )}
    </section>
  );
};

export const RLNavSidebar = () => {
  const t = useRacketLabAdminT();
  const { ctx } = useRLContext();
  const allowed = new Set(ctx?.sections || []);
  return (
    <nav
      data-testid="rl-admin-subnav"
      className="flex flex-col gap-4 border-b border-slate-200 pb-3 md:flex-row md:flex-wrap md:items-start md:gap-x-6 md:gap-y-3"
    >
      {ctx && GROUPS.map((g) => (<Group key={g.id} group={g} t={t} allowed={allowed} />))}
    </nav>
  );
};

export default RLNavSidebar;
